import React, { useCallback, useEffect, useState } from 'react';
import { login, requestPasswordReset, validateEmail } from '../services/AuthService';
import { redirectToCrmLogin } from '../../common/Auth';
import Register from './Register';
import { subscribeToNewsletter } from '../../newsletter/services/NewsletterService';
import { setNToken } from '../../utils/cookie';
import Icon from '../../common/components/Icon';
import { useTranslations } from '../../localization/useTranslations';
import SocialButton from '../../components/buttons/SocialButton/SocialButton';
import Button from '../../components/buttons/SimpleButton/Button';
import { AuthFormStage } from '../types/Auth';
import AuthenticationHeader from './AuthenticationHeader';
import { onLoginSuccess } from '../../common/LoggedOutUserActions';
import Dialog from '../../common/components/Dialog';

interface LoginFormProps {
  isFormInModal?: boolean; // need for redirections, also it's used in the activation page
  hasDynamicTitle?: boolean;
  withResetPasswordModal?: boolean;
  initialStage?: AuthFormStage;
  initialEmail?: string;
  showSnackbar?: (message: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  isFormInModal,
  hasDynamicTitle = false,
  withResetPasswordModal = true,
  initialStage = 'email',
  initialEmail = '',
  showSnackbar,
}) => {
  const [stage, setStage] = useState<AuthFormStage>(initialStage);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [newsLetters, setNewsLetters] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const [privacyText, setPrivacyText] = useState('');
  const [preFilledEmail, setPreFilledEmail] = useState('');
  const [previousUrl, setPreviousUrl] = useState('');
  const [resetModalOpen, setResetModalOpen] = useState(true);

  const translations = useTranslations();

  const getTitle = () => {
    if (hasDynamicTitle) {
      return <AuthenticationHeader stage={stage} />;
    } else {
      return null;
    }
  };

  const handleSetStage = (newStage: AuthFormStage) => {
    if (setStage && newStage) {
      setStage(newStage);
    }
  };

  const openSnackbar = (message: string) => {
    if (showSnackbar) {
      showSnackbar(message);
    }
  };

  useEffect(() => {
    const referrer = document.referrer;
    const myAccountSlug = window.myAccountPageSlug || 'my-account';
    const fallbackUrl = `${window.location.origin}/${myAccountSlug}`;

    setPreviousUrl(referrer ? referrer : fallbackUrl);

    fetchPrivacyText();
  }, []);

  const fetchPrivacyText = () => {
    const fetchedPrivacyText = window?.forbes_authentication?.privacyText || '';
    setPrivacyText(fetchedPrivacyText);
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateEmailForm = (email: string) => {
    const re = /\S+@\S+\.\S{2,}/;
    return re.test(email);
  };

  const loginSubmit = async () => {
    if (!email || !password) return;

    setError('');
    setIsLoading(true);

    try {
      const response = await login({ email, password });

      if (response.status === 'ok') {
        if (response.access.token) {
          setNToken(response.access.token);

          await subscribeToNewsletters();

          const pendingAction = localStorage.getItem('pendingAction');

          if (pendingAction) {
            const { actionType, id } = JSON.parse(pendingAction);
            await onLoginSuccess(actionType, id);
          }

          redirectToCrmLogin();

          if (!isFormInModal) {
            // Redirect the user back to the previous URL
            window.location.href = previousUrl;
          }
        } else {
          setIsLoading(false);
          setError(translations?.errorMessages?.invalidLoginCredentials || 'Invalid login credentials');
        }
      } else {
        setIsLoading(false);
        setError(translations?.errorMessages?.invalidLoginCredentials || 'Invalid login credentials');
      }
    } catch (error) {
      setIsLoading(false);
      setError(translations?.snackBarMessages?.somethingWentWrong || 'An error occurred. Please try again later.');
    }
  };

  const subscribeToNewsletters = async () => {
    if (newsLetters.length > 0) {
      for (const newsletter of newsLetters) {
        await subscribeToNewsletter(newsletter);
      }
    }
  };

  const handleContinue = useCallback(async () => {
    setError('');
    setIsLoading(true);
    setNewsLetters(localStorage.getItem('newsletters') ? JSON.parse(localStorage.getItem('newsletters') || '') : []);

    localStorage.removeItem('newsletters');

    if (email === '' || !validateEmailForm(email)) {
      setError(translations?.errorMessages?.invalidLoginCredentials || 'Invalid email address');
      setIsLoading(false);
      return;
    }

    try {
      const emailExists = await validateEmail(email);
      if (!emailExists || emailExists.status !== 'ok' || !emailExists.user) {
        handleSetStage('register');
        setIsLoading(false);
      } else {
        handleSetStage('password');

        setIsLoading(false);
        setError('');
      }
    } catch (e: any) {
      setError(e.message || 'Something went wrong');
      setIsLoading(false);
    }
  }, [email, translations]);

  const onEmailInputBackClick = () => {
    handleSetStage('email');
    setPassword('');
    setError('');
  };

  const passwordResetSubmit = async () => {
    setError('');
    setIsLoading(true);

    try {
      if (!email || !validateEmailForm(email)) {
        setError(translations?.errorMessages?.invalidLoginCredentials || 'Invalid email address');
        setIsLoading(false);
        return;
      }

      const response = await requestPasswordReset(email);

      window.localStorage.setItem('resetPasswordLocation', window.location.href.replace(window.location.search, ''));

      if (response.status === 'ok') {
        if (!withResetPasswordModal) {
          openSnackbar(
            translations?.snackBarMessages?.passReset || 'Password reset email sent, please check your inbox.'
          );
        }

        setIsLoading(false);

        // Note: Need to postpone the event so the snackbar can be shown

        handleSetStage('resetRequestSent');
      } else {
        setError('Something went wrong');
        setIsLoading(false);
      }
    } catch (error) {
      setError(translations?.snackBarMessages?.somethingWentWrong || 'An error occurred. Please try again later.');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setError('');
  }, [email, password]);

  // Prefill email in login modal if it's available in the subscriptionData
  useEffect(() => {
    const subscriptionData = localStorage.getItem('subscriptionData');
    if (subscriptionData) {
      const subDataObj = JSON.parse(subscriptionData);

      setPreFilledEmail(subDataObj?.payload?.email || '');
    }

    if (preFilledEmail && isFormInModal) {
      setEmail(preFilledEmail);
    }
  }, [preFilledEmail, isFormInModal]);

  useEffect(() => {
    if (preFilledEmail !== '') {
      handleContinue();
    }
  }, [preFilledEmail, handleContinue]);

  const loginMarkup = (
    <>
      {getTitle()}
      <div className="input-group">
        <label className={`input-group__label ${focused ? 'focused' : ''}`}>
          {translations?.authLabels?.emailLabel || 'Email'}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleContinue();
            }
          }}
          placeholder={translations?.authLabels?.emailPlaceholder || 'Email'}
          className={error ? 'input-group__input error' : 'input-group__input'}
          autoFocus
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoComplete="off"
        />
        {error && <label className="input-error">{error}</label>}
      </div>

      <Button
        className=""
        label={translations?.authLabels?.continue || 'Continue'}
        type="primary"
        onClick={handleContinue}
        size="large"
        isDisabled={isLoading}
        fullWidth={true}
      />

      <div className="authentication__bottom">
        <div className="separator">
          <span className="separator__text basic-14">{translations?.authLabels?.or || 'OR'}</span>
          <div className="separator__line" />
        </div>
        <div>
          <SocialButton provider="google" />
          {window.country !== 'hu' && <SocialButton provider="facebook" />}
        </div>
        <div className="authentication__privacy-text">
          <div dangerouslySetInnerHTML={{ __html: privacyText }} />
        </div>
      </div>
    </>
  );

  const passwordInputMarkup = (
    <>
      {getTitle()}
      <div className="login-modal__form login-modal__form--exist">
        <div className="input-group">
          <label className="input-group__label disabled">{translations?.authLabels?.emailLabel || 'Email'}</label>
          <input
            type="email"
            value={email}
            placeholder={translations?.authLabels?.emailPlaceholder || 'Email'}
            className={error ? 'input-group__input input-group__input--error error' : 'input-group__input'}
            disabled
          />
          <span className="input-group__back-link" onClick={onEmailInputBackClick}>
            {translations?.authLabels?.backLink || 'Back'}
          </span>
        </div>
        <div className="input-group">
          <label className={`input-group__label ${focused ? 'focused' : ''}`}>
            {translations?.authLabels?.passLabel || 'Password'}
          </label>
          <div className="password-input">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  loginSubmit();
                }
              }}
              placeholder={translations?.authLabels?.passwordPlaceholder || 'Password'}
              className={error ? 'input-group__input input-group__input--error error' : 'input-group__input'}
              autoFocus
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            <span className="password-toggle" onClick={togglePasswordVisibility}>
              {showPassword ? <Icon name="eye-off" size="medium" /> : <Icon name="eye-on" size="medium" />}
            </span>
          </div>
          {error && <label className="input-error">{error}</label>}
        </div>
        <div className="input-group">
          <Button
            className="login-modal__button"
            label={translations?.authLabels?.login || 'Login'}
            type="primary"
            size="large"
            onClick={loginSubmit}
            isDisabled={isLoading}
            fullWidth={true}
          />
        </div>

        <button
          className="login-modal__forgot-password link-underline-16"
          onClick={() => handleSetStage('resetPassword')}
        >
          {translations?.authLabels?.resetPassword || 'Forgot password?'}
        </button>
      </div>
    </>
  );

  const resetPasswordMarkup = (
    <>
      {getTitle()}
      <div className="input-group">
        <label className={`input-group__label ${focused ? 'focused' : ''}`}>
          {translations?.authLabels?.emailLabel || 'Email'}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              passwordResetSubmit();
            }
          }}
          placeholder={translations?.authLabels?.emailPlaceholder || 'Email'}
          className={error ? 'input-group__input error' : 'input-group__input'}
          autoFocus
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {error && <label className="input-error">{error}</label>}
      </div>

      <Button
        className=""
        label={translations?.authLabels?.resetPassword || 'Reset Password'}
        type="primary"
        onClick={() => passwordResetSubmit()}
        size="large"
        isDisabled={isLoading}
        fullWidth={true}
      />
    </>
  );

  const resetRequestSentMarkup = (
    <>
      {getTitle()}
      <div className="login-modal__form login-modal__form--exist">
        <div className={`login-modal__resend-password ${isLoading ? 'disabled' : ''}`}>
          <Icon name="refresh" />
          <span className="link-underline-16" onClick={() => passwordResetSubmit()}>
            {translations?.authLabels?.sendAgain || 'Send again'}
          </span>
        </div>
      </div>
      <Dialog isOpen={withResetPasswordModal && resetModalOpen} onClose={() => setResetModalOpen(false)}>
        <div className="modal modal--small login-modal">
          <div className="modal__wrapper">
            <div className="modal__header login-modal__header">
              <h5>{translations?.subscriptionActivation?.resetPasswordModalTitle || 'Password reset'}</h5>
              <button
                className="login-modal__close-button"
                onClick={() => setResetModalOpen(false)}
                aria-label="Close modal"
              >
                <Icon name="close" size="small" />
              </button>
            </div>

            <div className="login-modal__content modal__content">
              {translations?.subscriptionActivation?.resetPasswordModalDescription ||
                'You will receive a password reset link to your email. It should arrive within 2 minutes.'}
            </div>

            <div className="modal__footer login-modal__footer" style={{ justifyContent: 'flex-start', gap: '16px' }}>
              <button
                style={{ fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => setResetModalOpen(false)}
              >
                {translations?.subscriptionActivation?.resetPasswordModalButton || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );

  const renderStage = (stage: AuthFormStage) => {
    switch (stage) {
      case 'email':
        return loginMarkup;
      case 'password':
        return passwordInputMarkup;
      case 'register':
        return (
          <Register
            initialEmail={email}
            subscribeToNewsletters={subscribeToNewsletters}
            onBackLinkClick={onEmailInputBackClick}
            isFormInModal={isFormInModal}
            previousUrl={previousUrl}
          />
        );
      case 'resetPassword':
        return resetPasswordMarkup;
      case 'resetRequestSent':
        return resetRequestSentMarkup;
      default:
        return loginMarkup;
    }
  };

  document.getElementById('login-modal')?.addEventListener('close', () => {
    if (stage !== 'email') {
      handleSetStage('email');
      setPassword('');
      setNewsLetters([]);
    }
  });

  return renderStage(stage);
};
